import React, { useState } from 'react';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import { isArray } from 'lodash';

import { BannerState, InfoBanner } from '../../../Errors/InfoBanner';
import { ClientSummaryNode } from '../../../../generated/webapp_gql';
import ReminderSuccess from '../ReminderModal/ReminderSuccess/ReminderSuccess';
import { useSendMemberExtendTherapyEmailReminderMutation } from './documents/SendMemberExtendTherapyEmailReminder.generated';
import { ReactComponent as Icon } from '../../../../assets/svgs/icon-send-extend-therapy-email-reminder.svg';
import CancelSessionButton from '../ReminderModal/SendReminder/CancelSessionButton/CancelSessionButton';
import SendReminderButton from '../ReminderModal/SendReminder/SendReminderButton/SendReminderButton';
import { HeaderTestID, BodyTestID, SuccessBodyTestID } from './constants';

export interface Props {
  member: ClientSummaryNode;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onCancel: () => void;
}
const ExtendTherapyReminder: React.FC<Props> = ({ member, memberClient, onCancel }) => {
  const [errorMessage, setErrorMessage] = useState('');

  const [sendMemberExtendTherapyEmailReminderMutation, { data, error, loading: isSubmitting }] =
    useSendMemberExtendTherapyEmailReminderMutation({
      client: memberClient,
      onError: (error) => {
        if (
          error?.graphQLErrors &&
          isArray(error.graphQLErrors) &&
          error.graphQLErrors.length > 0
        ) {
          setErrorMessage(error.graphQLErrors[0].message ?? '');
        }
      },
      variables: {
        memberUuid: member.memberUuid,
      },
    });

  const isSuccess = !isSubmitting && !error && data?.sendMemberExtendTherapyEmailReminder?.ok;

  return (
    <div className="text-center p-4">
      {!isSuccess && errorMessage.length > 0 && (
        <InfoBanner bannerState={BannerState.Error} message={errorMessage} />
      )}

      {!isSuccess && (
        <>
          <div className="mb-6 flex justify-center">
            <Icon />
          </div>

          <div className="text-gray-700 text-2xl mb-6 font-bold" data-testid={HeaderTestID}>
            Your client needs to check their health plan coverage before you can book additional
            sessions
          </div>

          <div className="font-normal text-gray-800 text-base mb-6" data-testid={BodyTestID}>
            Your client has run out of complimentary therapy sessions. To book more sessions, we
            need to check their health plan details for extended therapy eligibility. You can send
            them a reminder to complete this information.
          </div>

          <div className="flex justify-center">
            <CancelSessionButton
              className="mr-1"
              isDisabled={isSubmitting}
              isInProgress={false}
              onClick={onCancel}
              text="Cancel"
            />

            <SendReminderButton
              className="ml-1"
              isInProgress={isSubmitting}
              onClick={sendMemberExtendTherapyEmailReminderMutation}
              text="Send reminder"
            />
          </div>
        </>
      )}

      {isSuccess && (
        <ReminderSuccess>
          <div className="font-normal text-gray-800 text-base mb-6" data-testid={SuccessBodyTestID}>
            Once your client submits their health plan details, we’ll email you with updates on
            their coverage status so you can book extended therapy sessions if they are eligible.
          </div>
        </ReminderSuccess>
      )}
    </div>
  );
};

export default ExtendTherapyReminder;
